import {
  ascend,
  compose,
  prop,
  propOr,
  sortWith,
  toLower,
  includes,
  map,
  reduce,
  keys,
  min,
  pipe,
  propEq,
  reject,
  isNil,
  concat,
  flatten,
  both,
  find,
  filter,
  partition,
  identity,
} from 'ramda';

import {
  MY_LOBBY_GROUP_ID,
  CUSTOM_SORT_CONFIG,
  TABLES_SORTING_TYPE_CONFIG,
} from '../../../constants/minilobby';
import { GameGroup } from '../../reducers/config';
import { LobbyTable } from '../../reducers/lobby';

const sortBJTablesBySeatAvailability = sortWith<LobbyTable>([
  // sort only BJ/ABJ tables by their seat availability (tables with free seats come first)
  ascend(
    ({ gameId, freeSeatsCount }) => [ 1, 10 ].includes(gameId) && freeSeatsCount === 0
  ),
]);

/**
 * Sort tables putting the ones from group config `tables` first,
 * then adding tables according to their game id position in group config `games`.
 * This sorting is applied when BO config `tables_sorting_type` is `default`
 */
const sortTablesByGameId = ({
  groupLiveTables,
  groupConfig,
}: {
  groupLiveTables: LobbyTable[];
  groupConfig: GameGroup;
}) => reject(
  isNil,
  concat(
    map(
      (id) => find(propEq('id', `${id}`))(groupLiveTables),
      groupConfig.tables
    ) as LobbyTable[],
    flatten(
      map(
        (gameId) => filter(
          both(
            propEq('gameId', gameId),
            ({ id }: LobbyTable) => !groupConfig.tables.includes(Number(id))
          )
        )(groupLiveTables),
        groupConfig.games
      )
    )
  )
);

/**
 * Sort tables putting the ones available in current language first, then adding the rest.
 * Each of the 2 subsets are then sorted using the `default` rule.
 * This sorting is applied when BO config `tables_sorting_type` is `language`
 * @see sortTablesByGameId
 */
const sortTablesByLanguage = ({ groupLiveTables, groupConfig, language }: {
  groupLiveTables: LobbyTable[];
  groupConfig: GameGroup;
  language: string;
}) => flatten(
  map(
    (tablesSortedByLanguage) => sortTablesByGameId({
      groupLiveTables: tablesSortedByLanguage,
      groupConfig,
    }),
    partition<LobbyTable>(pipe(prop('languages'), includes(language)), groupLiveTables)
  )
);

/**
 * Sort tables in ascending order by their smallest `Min_Bet`.
 * Tables having the same smallest `Min_Bet` are sorted in ascending order by their smallest `Max_Bet`.
 * This sorting is applied when BO config `tables_sorting_type` is `limits`
 */
const sortTablesByLimits = sortWith<any>([
  ascend(pipe(prop('limits'), map(prop('minBet')), reduce(min, Infinity))),
  ascend(pipe(prop('limits'), map(prop('maxBet')), reduce(min, Infinity))),
]);

const shouldSortGroupTables = (groupName: string) => (customSortingConfig: Record<string, unknown>) => {
  const shouldSort = customSortingConfig[CUSTOM_SORT_CONFIG.ENABLE_SORT_PROP] ?? false;
  const sortGroupName = compose(
    toLower,
    String,
    propOr(null, CUSTOM_SORT_CONFIG.GROUP_NAME_PROP)
  )(customSortingConfig);

  return shouldSort && groupName === sortGroupName;
};

const getGroupCustomSortFn = (sortRule: string) => {
  switch (sortRule) {
  case 'BJ_BY_SEATS':
    return sortBJTablesBySeatAvailability;
  default:
    return identity;
  }
};

type SortGroupTablesParams = {
  groupId: number;
  groupLiveTables: LobbyTable[];
  groupConfig: GameGroup;
  customSortingConfigs: Record<string, unknown>[];
  tablesSortingTypeConfig: string;
  language: string;
};

/**
 * Sort tables from a table group
 * - if table group is My Lobby, no sorting is done
 */
export const sortGroupTables = ({
  groupId,
  groupLiveTables,
  groupConfig,
  customSortingConfigs,
  tablesSortingTypeConfig,
  language,
}: SortGroupTablesParams) => {
  if (groupId === MY_LOBBY_GROUP_ID) {
    return groupLiveTables;
  }

  const groupName = compose(toLower, String, prop('name'))(groupConfig);

  let sortedTables;

  switch (tablesSortingTypeConfig) {
  case TABLES_SORTING_TYPE_CONFIG.DEFAULT: {
    sortedTables = sortTablesByGameId({ groupLiveTables, groupConfig });
    break;
  }
  case TABLES_SORTING_TYPE_CONFIG.LANGUAGE: {
    sortedTables = sortTablesByLanguage({
      groupLiveTables,
      groupConfig,
      language,
    });
    break;
  }
  case TABLES_SORTING_TYPE_CONFIG.LIMITS: {
    sortedTables = sortTablesByLimits(groupLiveTables);
    break;
  }
  default:
    sortedTables = groupLiveTables;
    break;
  }

  sortedTables = pipe(
    filter(shouldSortGroupTables(groupName)),
    keys,
    map(getGroupCustomSortFn),
    reduce((acc, fn) => fn(acc), sortedTables)
  )(customSortingConfigs);

  return sortedTables;
};
